import { bookmarkService as firebaseBookmarkService } from './bookmarkService';
import { offlineStorageService } from './offlineStorageService';
import { networkService } from './networkService';
import { syncService } from './syncService';

class OfflineBookmarkService {
  constructor() {
    this.isOnline = networkService.getNetworkStatus();
    
    // Listen for network changes
    networkService.addNetworkListener((isOnline) => {
      this.isOnline = isOnline;
      // No automatic sync trigger from here; syncService handles its own triggers.
    });
  }

  // Get bookmarks - tries online first, falls back to offline
  async getBookmarks(userId) {
    // console.log(`🔍 OFFLINE_BOOKMARK_SERVICE: getBookmarks called. User: ${userId}, Online: ${this.isOnline}`);
    
    if (this.isOnline) {
      try {
        // console.log('📡 OFFLINE_BOOKMARK_SERVICE: Online. Fetching directly from firebaseBookmarkService...');
        const serverBookmarks = await firebaseBookmarkService.getBookmarks(userId);
        // console.log(`✅ OFFLINE_BOOKMARK_SERVICE: Fetched ${serverBookmarks.length} bookmarks directly from server. These will be returned directly.`);
        // Return server bookmarks directly. 
        // The local offlineStorageService will be updated by syncService after reconciliation.
        // DO NOT update offlineStorageService from here when online, to prevent wiping unsynced local changes.
        return serverBookmarks;
      } catch (error) {
        console.warn('❌ OFFLINE_BOOKMARK_SERVICE: Error fetching from server while online. Falling back to offline storage. Error:', error);
        // Fallback to offline storage if server fetch fails
        const offlineBookmarks = await offlineStorageService.getBookmarks(userId);
        // console.log(`⚠️ OFFLINE_BOOKMARK_SERVICE: Fallback - Fetched ${offlineBookmarks.length} from offline storage.`);
        return offlineBookmarks;
      }
    } else {
      // console.log('💾 OFFLINE_BOOKMARK_SERVICE: Offline. Fetching from offlineStorageService...');
      const offlineBookmarks = await offlineStorageService.getBookmarks(userId);
      // console.log(`📱 OFFLINE_BOOKMARK_SERVICE: Offline - Fetched ${offlineBookmarks.length} bookmarks from offline storage.`);
      return offlineBookmarks;
    }
  }

  // Add bookmark - simple: if online save to server, if offline save locally
  async addBookmark(bookmarkData, userId) {
    if (this.isOnline) {
      try {
        // First, check if bookmark already exists on the server
        const existsOnServer = await firebaseBookmarkService.checkIfBookmarkExists(bookmarkData.url, userId);
        if (existsOnServer) {
          throw new Error('Bookmark with this URL already exists on the server.');
        }

        // If online and not a duplicate, save directly to server
        const serverBookmark = await firebaseBookmarkService.addBookmark(bookmarkData, userId);
        
        // Also save to offline storage for caching
        await offlineStorageService.addBookmark(serverBookmark, userId);
        
        return serverBookmark;
      } catch (error) {
        // Fall through to offline logic, but ensure it's marked as an offline creation
        console.warn('OFFLINE_BOOKMARK_SERVICE: Failed to add bookmark to server, saving offline. Error:', error.message);
      }
    }
    
    // Offline mode or server save failed - save locally only
    // console.log(`OFFLINE_BOOKMARK_SERVICE: Saving bookmark to offline storage (isOffline will be true). Title: ${bookmarkData.title}`);
    const offlineData = { ...bookmarkData, isOffline: true };
    const offlineBookmark = await offlineStorageService.addBookmark(offlineData, userId);
    
    return offlineBookmark;
  }

  // Update bookmark - simple: if online update server, if offline just mark as updated
  async updateBookmark(id, updates, userId) {
    if (this.isOnline) {
      try {
        // Try to update on server first
        const serverBookmark = await firebaseBookmarkService.updateBookmark(id, updates, userId);
        // Update offline storage with server response
        await offlineStorageService.updateBookmark(id, serverBookmark, userId);
        return serverBookmark;
      } catch (error) {
        console.warn('Failed to update bookmark on server, saving offline:', error);
        // Fall through to offline logic
      }
    }
    
    // Offline mode - just update locally and mark as needing sync
    const updatedBookmark = await offlineStorageService.updateBookmark(id, updates, userId);
    return updatedBookmark;
  }

  // Delete bookmark - simple: if online delete from server, if offline just mark as deleted
  async deleteBookmark(id, userId) {
    if (this.isOnline) {
      try {
        // Try to delete from server first
        await firebaseBookmarkService.deleteBookmark(id, userId);
        // Remove from offline storage
        await offlineStorageService.deleteBookmark(id, userId);
        return id;
      } catch (error) {
        console.warn('Failed to delete bookmark from server, deleting offline:', error);
        // Fall through to offline logic
      }
    }
    
    // Offline mode - just delete locally
    await offlineStorageService.deleteBookmark(id, userId);
    return id;
  }

  // Search bookmarks - works offline
  async searchBookmarks(searchTerm, userId) {
    // Use offline storage for search as it's faster and works offline
    return await offlineStorageService.searchBookmarks(searchTerm, userId);
  }

  // Force refresh from server
  async refreshBookmarks(userId) {
    if (!this.isOnline) {
      throw new Error('Cannot refresh bookmarks while offline');
    }
    
    try {
      const serverBookmarks = await firebaseBookmarkService.getBookmarks(userId);
      await offlineStorageService.saveBookmarks(serverBookmarks, userId);
      return serverBookmarks;
    } catch (error) {
      console.error('Failed to refresh bookmarks:', error);
      // Return offline data as fallback
      return await offlineStorageService.getBookmarks(userId);
    }
  }

  // Check if offline data exists
  async hasOfflineData(userId) {
    return await offlineStorageService.hasOfflineData(userId);
  }

  // Get sync status
  getSyncStatus() {
    return syncService.getSyncStatus();
  }

  // Force sync
  async forceSync(userId) {
    return await syncService.forcSync(userId);
  }

  // Check for pending changes
  async hasPendingChanges(userId) {
    return await syncService.hasPendingChanges(userId);
  }
}

export const offlineBookmarkService = new OfflineBookmarkService();
